import { ArrowLeftRight } from 'lucide-react';
import { Avatar, AvatarFallback } from '../tailgrids/core/avatar.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuHeader,
  DropdownMenuItem,
  DropdownMenuSection,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../tailgrids/core/dropdown.js';
import { useAuthStore } from '../../store/index.js';
import { useLinkedAccounts, useLogout, useSwitchAccount } from '../../hooks/useAccountSession.js';
import { AltArrowDownIcon, GearIcon, LogoutIcon } from './shell-icons.js';

function initialOf(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?';
}

/**
 * The account menu: who is signed in, settings, linked-account switching and sign out.
 * Template example: NextAdmin `components/common/header/user-profile.tsx` (commit e0b72a9aa69e).
 */
export function UserMenu() {
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const { accounts: linkedAccounts, failed: linkedAccountsFailed } = useLinkedAccounts();
  const switchAccount = useSwitchAccount();

  if (!user) return null;
  const displayName = user.fullName || user.username || user.email;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Account menu"
        className="group flex items-center gap-2.5 rounded-lg border-0 p-0 transition-all outline-none focus-visible:ring-4 focus-visible:ring-input-primary-focus-border/20 focus-visible:ring-offset-1"
      >
        <Avatar>
          <AvatarFallback className="size-10 rounded-lg border border-border-secondary-alt bg-background-gray-secondary_alt">
            {initialOf(displayName)}
          </AvatarFallback>
        </Avatar>

        <span className="hidden text-sm leading-5 font-medium text-text-primary sm:inline">{displayName}</span>

        <AltArrowDownIcon className="text-icon-tertiary transition-transform duration-200 group-aria-expanded:-rotate-180" />
      </DropdownMenuTrigger>

      <DropdownMenuContent placement="bottom end" className="w-70 overflow-hidden p-0 shadow-3xl">
        <DropdownMenuHeader className="flex w-full items-center justify-start gap-2 border-b border-border-secondary-alt px-4 py-3">
          <Avatar size="md">
            <AvatarFallback className="border border-border-secondary-alt bg-background-gray-secondary_alt">
              {initialOf(displayName)}
            </AvatarFallback>
          </Avatar>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium text-text-primary">{displayName}</span>
            <span className="truncate text-xs text-text-tertiary">{user.email}</span>
          </span>
        </DropdownMenuHeader>

        <DropdownMenuSection className="p-1.5">
          <DropdownMenuItem href="/settings" className="cursor-pointer px-3 py-2.5">
            <span className="shrink-0 text-icon-secondary group-hover:text-text-primary">
              <GearIcon />
            </span>
            <span className="leading-5 font-medium">Settings</span>
          </DropdownMenuItem>
        </DropdownMenuSection>

        {linkedAccounts.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSection className="p-1.5" aria-label="Switch account">
              <DropdownMenuHeader className="px-3 pt-1 pb-1.5 text-xs text-text-tertiary uppercase">
                Switch account
              </DropdownMenuHeader>
              {linkedAccounts.map((account) => (
                <DropdownMenuItem
                  key={account.userId}
                  id={`account-${account.userId}`}
                  textValue={account.email}
                  isDisabled={switchAccount.isPending}
                  onAction={() => switchAccount.mutate(account.userId)}
                  className="cursor-pointer px-3 py-2"
                >
                  <ArrowLeftRight size={16} className="shrink-0 text-icon-secondary" />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-sm text-text-primary">{account.email}</span>
                    <span className="truncate text-xs text-text-tertiary">
                      {account.organizationName}
                      {account.fullName ? ` — ${account.fullName}` : ''}
                    </span>
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuSection>
          </>
        )}

        {linkedAccountsFailed && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSection className="p-1.5" aria-label="Switch account">
              <DropdownMenuItem
                id="linked-accounts-unavailable"
                isDisabled
                className="px-3 py-2 text-xs text-text-tertiary"
              >
                Linked accounts could not be loaded. Reopen the menu to retry.
              </DropdownMenuItem>
            </DropdownMenuSection>
          </>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem onAction={logout} className="m-1.5 w-auto cursor-pointer px-3 py-2.5">
          <span className="text-icon-secondary group-hover:text-text-primary">
            <LogoutIcon />
          </span>
          <span className="leading-5">Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
